import {
  createApiErrorFromResponse,
  readApiErrorDetail,
} from "@airange/shared/api";
import { resolveCtfApiEndpoint } from "./env";
import type { CtfCurrentUser } from "./ctf-contract";
import { clearSession } from "./session";

type CtfAdminInitialState = {
  authStatus?: CtfAdminAuthStatus;
  currentUser?: CtfCurrentUser | null;
  ctfUser?: CtfCurrentUser | null;
  [key: string]: unknown;
};

export type CtfAdminAuthStatus = "authenticated" | "no_session" | "no_access";

export type OidcCallbackCompleteResult = {
  redirectPath: string;
};

export { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";

const CTF_ADMIN_APPLICATION_KEY = "ctf-admin";

function authEndpoint(path: "/authorize" | "/callback" | "/bootstrap" | "/logout") {
  return resolveCtfApiEndpoint(`/api/auth/${CTF_ADMIN_APPLICATION_KEY}${path}`);
}

function applicationHeaders() {
  return { "X-AIRange-Application-Key": CTF_ADMIN_APPLICATION_KEY };
}

function requireCtfAdminInitialState<T extends CtfAdminInitialState>(state: T | undefined): T {
  if (!state) {
    throw new Error("CTF admin initial state is required before session state merge");
  }
  return state;
}

async function parseJson<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

export function consumeOidcRedirectPath(defaultPath = "/dashboard") {
  return defaultPath;
}

export async function startOidcLogin(redirectPath: string, prompt: "none" | "login" = "login", fetchImpl: typeof fetch = fetch) {
  const response = await fetchImpl(authEndpoint("/authorize"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...applicationHeaders() },
    body: JSON.stringify({ return_path: redirectPath, prompt }),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  const result = await parseJson<{ authorization_url: string }>(response);
  window.location.assign(result.authorization_url);
}

export async function completeOidcCallback(_code: string, _state: string, fetchImpl: typeof fetch = fetch): Promise<OidcCallbackCompleteResult> {
  const response = await fetchImpl(authEndpoint("/callback"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...applicationHeaders() },
    body: JSON.stringify({ code: _code, state: _state }),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  const result = await parseJson<{ redirect_path: string }>(response);
  return { redirectPath: result.redirect_path };
}

export async function bootstrapCtfAppSession(fetchImpl: typeof fetch = fetch) {
  const bootstrapResponse = await fetchImpl(authEndpoint("/bootstrap"), {
    method: "POST",
    credentials: "include",
    headers: applicationHeaders(),
  });
  if (!bootstrapResponse.ok) {
    throw createApiErrorFromResponse(bootstrapResponse, await readApiErrorDetail(bootstrapResponse));
  }
  return await parseJson<CtfCurrentUser | null>(bootstrapResponse);
}

export async function bootstrapSession(fetchImpl: typeof fetch = fetch) {
  try {
    return await bootstrapCtfAppSession(fetchImpl);
  } catch (error) {
    clearSession();
    throw error;
  }
}

export async function logout(fetchImpl: typeof fetch = fetch) {
  const response = await fetchImpl(authEndpoint("/logout"), {
    method: "POST",
    credentials: "include",
    headers: applicationHeaders(),
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  clearSession();
}

export function syncCtfAdminInitialState<T extends CtfAdminInitialState>(
  state: T | undefined,
  next: {
    currentUser: CtfCurrentUser;
    ctfUser: CtfCurrentUser | null;
  },
): T {
  const currentState = requireCtfAdminInitialState(state);
  return {
    ...currentState,
    authStatus: "authenticated",
    currentUser: next.currentUser,
    ctfUser: next.ctfUser,
  } as T;
}

export function clearCtfAdminInitialState<T extends CtfAdminInitialState>(
  state: T | undefined,
  overrides: {
    authStatus?: CtfAdminAuthStatus;
    currentUser?: CtfCurrentUser | null;
  } = {},
): T {
  const currentState = requireCtfAdminInitialState(state);
  return {
    ...currentState,
    authStatus: overrides.authStatus ?? "no_session",
    currentUser: overrides.currentUser ?? null,
    ctfUser: null,
  } as T;
}
